var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Link } from 'react-router-dom';
import { lighten, darken, getLuminance } from 'polished';
import isFunction from 'lodash/isFunction';
import styled, { css, keyframes, ThemeProvider, useTheme } from 'styled-components';
import { color, defaultTheme } from '@socgress/ui/themes';
import { zord } from '@socgress/lib/zord';
import { ifProp, prop, theme } from 'styled-tools';
var getHoverColorByThemeColor = function (color) {
    return getLuminance(color) > 0.7 ? darken(0.03, color) : darken(0.02, color);
};
var getHoverColorBySiteThemeColor = function (siteColor) {
    return function (_a) {
        var theme = _a.theme;
        return getHoverColorByThemeColor(theme.colors[siteColor]);
    };
};
var themes = {
    green: '#12c78c',
    gray: '#fff',
    vk: '#5e98df',
    instagram: '#a772db',
    youtube: '#f6543f',
    twitter: '#22aafb',
    ok: '#ee8208',
    telegram: '#4BAEFA',
    blue: {
        bgColor: color('surfaceBrand'),
        textColor: color('textOnBrand'),
        hoverTextColor: getHoverColorBySiteThemeColor('textOnBrand'),
    },
    transparent: {
        bgColor: 'transparent',
        textColor: color('textBrand'),
        hoverTextColor: getHoverColorBySiteThemeColor('textBrand'),
        activeTextColor: darken(0.02, '#6099FA'),
    },
    white: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('textBrand'),
        hoverTextColor: getHoverColorBySiteThemeColor('textBrand'),
        activeTextColor: darken(0.02, '#6099FA'),
    },
    whitePromo: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('textPromo'),
        hoverTextColor: getHoverColorBySiteThemeColor('textPromo'),
        activeTextColor: getHoverColorBySiteThemeColor('textPromo'),
    },
    whiteBlue: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('textBrand'),
        hoverTextColor: getHoverColorBySiteThemeColor('textBrand'),
        activeBgColor: darken(0.03, '#5e98df'),
        activeTextColor: '#fff',
    },
    whiteDanger: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('textError'),
        hoverTextColor: getHoverColorBySiteThemeColor('textError'),
    },
    whiteGreen: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('textSuccess'),
        hoverTextColor: getHoverColorBySiteThemeColor('textSuccess'),
        activeBgColor: darken(0.03, '#5e98df'),
        activeTextColor: '#fff',
    },
    transparentGreen: {
        bgColor: 'transparent',
        textColor: '#12c78c',
        borderColor: '#cdf3d8',
        hoverTextColor: lighten(0.02, '#12c78c'),
        hoverBorderColor: lighten(0.04, '#12c78c'),
        activeTextColor: darken(0.03, '#12c78c'),
    },
    transparentBlue: {
        bgColor: 'transparent',
        textColor: color('textOnBrand'),
        borderColor: '#d1e6fe',
        hoverTextColor: lighten(0.02, '#6099FA'),
        hoverBorderColor: lighten(0.04, '#6099FA'),
        activeTextColor: darken(0.03, '#6099FA'),
    },
    link: {
        bgColor: 'transparent',
        textColor: color('textBrand'),
        hoverTextColor: lighten(0.1, '#6099FA'),
        activeTextColor: darken(0.02, '#6099FA'),
        boxShadow: 'none',
        hoverBoxShadow: 'none',
        activeBoxShadow: 'none',
    },
    packOfDay: {
        bgColor: 'transparent',
        textColor: '#fff',
        boxShadow: '0 0 rgba(255,255,255, 0.001)',
        borderColor: '#294577',
        hoverBgColor: '#5e98df',
        hoverTextColor: '#fff',
        hoverBorderColor: '#5e98df',
        activeBgColor: darken(0.03, '#5e98df'),
        activeTextColor: '#fff',
    },
    lightBlue: {
        bgColor: '#F6F8FB',
        textColor: '#6099FA',
        boxShadow: 'none',
        hoverBoxShadow: 'none',
        activeBoxShadow: 'none',
        hoverBgColor: '#F6F8FB',
        hoverTextColor: lighten(0.02, '#6099FA'),
        activeBgColor: darken(0.03, '#F6F8FB'),
        activeTextColor: '#6099FA',
    },
    danger: {
        bgColor: color('surfaceBackgroundSecondary'),
        textColor: color('surfaceError'),
        hoverBgColor: color('surfaceError'),
        hoverTextColor: '#fff',
        hoverBorderColor: defaultTheme.colors.surfaceError,
        activeBorderColor: darken(0.03, defaultTheme.colors.surfaceError),
        activeBgColor: darken(0.03, defaultTheme.colors.surfaceError),
        activeTextColor: darken(0.03, '#fff'),
    },
};
function getButtonTheme(themeName, appTheme, withBorder) {
    var isObjectTheme = Object.keys(themes).indexOf(themeName) !== -1;
    return isObjectTheme
        ? createButtonTheme(themes[themeName], appTheme, withBorder)
        : createButtonTheme(themeName, appTheme, withBorder);
}
function createButtonTheme(buttonTheme, currentTheme, withBorder) {
    if (withBorder === void 0) { withBorder = false; }
    var isSimpleTheme = typeof buttonTheme === 'string';
    var themeBgColor = isSimpleTheme
        ? buttonTheme
        : buttonTheme.bgColor;
    var bgColor = isFunction(themeBgColor) ? themeBgColor({ theme: currentTheme }) : themeBgColor;
    var defaultHoverBgColor = lighten(0.04, bgColor);
    var defaultActiveBgColor = darken(0.03, bgColor);
    var defaultBorderColor = currentTheme.borders.button;
    // It is not style nor css object, it just style scheme
    var defaultTheme = {
        textColor: '#fff',
        hoverTextColor: '#fff',
        activeTextColor: '#fff',
        borderColor: defaultBorderColor,
        hoverBorderColor: defaultBorderColor,
        bgColor: bgColor,
        hoverBgColor: defaultHoverBgColor,
        activeBgColor: defaultActiveBgColor,
        boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.1)',
        hoverBoxShadow: '0 0 15px 5px rgba(0, 56, 110, 0.08)',
        activeBoxShadow: 'inset 0 0 5px 5px rgba(0, 56, 110, 0.05)',
        withBorder: withBorder,
        buttonTheme: buttonTheme,
    };
    if (isSimpleTheme) {
        return defaultTheme;
    }
    return __assign(__assign({}, defaultTheme), buttonTheme);
}
export function hoverState(_a) {
    var buttonTheme = _a.buttonTheme, theme = _a.theme, withBorder = _a.withBorder;
    var _b = getButtonTheme(buttonTheme, theme, withBorder), bgColor = _b.bgColor, hoverBgColor = _b.hoverBgColor, hoverBorderColor = _b.hoverBorderColor, hoverTextColor = _b.hoverTextColor, hoverBoxShadow = _b.hoverBoxShadow;
    var activeBoxShadows = [hoverBoxShadow];
    if (withBorder) {
        activeBoxShadows.push("inset 0 0 0 1px " + hoverBorderColor);
    }
    var parsedHoverTextColor = hoverTextColor ? (typeof hoverTextColor === 'function' ? hoverTextColor({ theme: theme }) : hoverTextColor) : '#fff';
    return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    box-shadow: ", ";\n    background-color: ", ";\n    color: ", ";\n  "], ["\n    box-shadow: ", ";\n    background-color: ", ";\n    color: ", ";\n  "])), activeBoxShadows.join(', '), hoverBgColor || lighten(0.04, bgColor), parsedHoverTextColor);
}
export function activeState(_a) {
    var buttonTheme = _a.buttonTheme, withBorder = _a.withBorder, theme = _a.theme;
    var _b = getButtonTheme(buttonTheme, theme, withBorder), activeBgColor = _b.activeBgColor, activeBoxShadow = _b.activeBoxShadow, borderColor = _b.borderColor, activeTextColor = _b.activeTextColor;
    var activeBoxShadows = [activeBoxShadow];
    if (withBorder) {
        activeBoxShadows.push("inset 0 0 0 1px " + (isFunction(borderColor) ? borderColor(theme) : borderColor));
    }
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    box-shadow: ", ";\n    background-color: ", ";\n    color: ", ";\n  "], ["\n    box-shadow: ", ";\n    background-color: ", ";\n    color: ", ";\n  "])), activeBoxShadows.join(', '), activeBgColor, activeTextColor);
}
export var Button = function (_a) {
    var as = _a.as, buttonTheme = _a.buttonTheme, withBorder = _a.withBorder, children = _a.children, stretch = _a.stretch, props = __rest(_a, ["as", "buttonTheme", "withBorder", "children", "stretch"]);
    var appTheme = useTheme();
    var theme = getButtonTheme(buttonTheme, appTheme, withBorder);
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement(ButtonComponent, __assign({ buttonTheme: buttonTheme, withBorder: withBorder, stretch: stretch }, props, { as: as }), children)));
};
export var ButtonComponent = styled.button.withConfig({ displayName: "ButtonComponent", componentId: "sc-8sj8fi" })(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  position: relative;\n\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n\n  font-size: 14px;\n  line-height: 1.214em;\n\n  box-shadow: ", ";\n  color: ", ";\n  background-color: ", ";\n\n  ", ";\n\n  &:not(:disabled) {\n    &:hover {\n      ", ";\n    }\n\n    &:active {\n      ", ";\n    }\n  }\n\n  ", ";\n  \n  ", "\n\n  border: 0;\n  cursor: pointer;\n  border-radius: 10px;\n  text-decoration: none;\n\n  font-weight: 500;\n  text-align: center;\n\n  transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,\n  border-color 0.15s ease-in-out, color 0.15s;\n\n  &[disabled] {\n    cursor: default;\n    background-color: ", ";\n    color: #fff;\n    box-shadow: none;\n  }\n"], ["\n  position: relative;\n\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n\n  font-size: 14px;\n  line-height: 1.214em;\n\n  box-shadow: ", ";\n  color: ", ";\n  background-color: ", ";\n\n  ",
    ";\n\n  &:not(:disabled) {\n    &:hover {\n      ", ";\n    }\n\n    &:active {\n      ", ";\n    }\n  }\n\n  ",
    ";\n  \n  ",
    "\n\n  border: 0;\n  cursor: pointer;\n  border-radius: 10px;\n  text-decoration: none;\n\n  font-weight: 500;\n  text-align: center;\n\n  transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,\n  border-color 0.15s ease-in-out, color 0.15s;\n\n  &[disabled] {\n    cursor: default;\n    background-color: ", ";\n    color: #fff;\n    box-shadow: none;\n  }\n"])), theme('boxShadow'), theme('textColor'), theme('bgColor'), ifProp('withBorder', css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n            box-shadow: ", ", inset 0 0 0 1px ", ";\n          "], ["\n            box-shadow: ", ", inset 0 0 0 1px ", ";\n          "])), function (p) { return p.theme.variant === 'accessible' ? theme('shadow.S') : theme('boxShadow'); }, theme('borderColor'))), hoverState, activeState, function (props) {
    return props.stretch
        ? css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n                    padding: 0.821em 0;\n                    width: 100%;\n                    max-width: 100%;\n                  "], ["\n                    padding: 0.821em 0;\n                    width: 100%;\n                    max-width: 100%;\n                  "]))) : css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n                    padding: 0.821em 1.2em;\n                    width: auto;\n                  "], ["\n                    padding: 0.821em 1.2em;\n                    width: auto;\n                  "])));
}, ifProp('isSmall', css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n    padding: ", ";\n  "], ["\n    padding: ", ";\n  "])), zord(2, 3))), color('surfaceDisabled'));
export var ButtonLink = function (props) { return (React.createElement(Button, __assign({}, props, { as: Link }))); };
ButtonLink.defaultProps = Button.defaultProps = {
    buttonTheme: 'green',
    withBorder: false,
    stretch: false,
};
var Loader = styled.div.withConfig({ displayName: "Loader", componentId: "sc-18xra8v" })(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  height: 18px;\n  align-items: center;\n"], ["\n  display: flex;\n  justify-content: center;\n  height: 18px;\n  align-items: center;\n"])));
var animation = keyframes(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n  0%,\n  80%,\n  100% {\n    opacity: 0.25;\n  }\n\n  40% {\n    opacity: 1;\n  }\n"], ["\n  0%,\n  80%,\n  100% {\n    opacity: 0.25;\n  }\n\n  40% {\n    opacity: 1;\n  }\n"])));
export var LoadingAnimation = styled.div.withConfig({ displayName: "LoadingAnimation", componentId: "sc-kbff2b" })(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n  width: 8px;\n  height: 8px;\n  margin: 0 2px;\n  background-color: ", ";\n  border-radius: 50%;\n  animation: ", " 1s infinite ease-in-out both;\n\n  &:nth-child(1) {\n    animation-delay: -0.3s;\n  }\n\n  &:nth-child(2) {\n    animation-delay: -0.15s;\n  }\n"], ["\n  width: 8px;\n  height: 8px;\n  margin: 0 2px;\n  background-color: ", ";\n  border-radius: 50%;\n  animation: ", " 1s infinite ease-in-out both;\n\n  &:nth-child(1) {\n    animation-delay: -0.3s;\n  }\n\n  &:nth-child(2) {\n    animation-delay: -0.15s;\n  }\n"])), function (props) { return props.color || '#fff'; }, animation);
export function ButtonLoader(_a) {
    var color = _a.color, style = _a.style;
    return (React.createElement(Loader, { style: style },
        React.createElement(LoadingAnimation, { color: color }),
        React.createElement(LoadingAnimation, { color: color }),
        React.createElement(LoadingAnimation, { color: color })));
}
ButtonLoader.defaultProps = {
    color: '#fff',
    style: {},
};
export var FormGroup = styled.div.withConfig({ displayName: "FormGroup", componentId: "sc-11c3w4k" })(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n  & + & {\n    margin-top: ", ";\n  }\n"], ["\n  & + & {\n    margin-top: ", ";\n  }\n"])), zord(5));
export var ButtonGroupDivider = styled.div.withConfig({ displayName: "ButtonGroupDivider", componentId: "sc-s8deux" })(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n  margin: ", " 0;\n  height: ", ";\n  position: relative;\n  text-align: center;\n  background-color: ", ";\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:before {\n    position: absolute;\n    content: \"\";\n    left: 0;\n    right: 0;\n    top: calc(50% - 1px);\n    height: 1px;\n    background-color: ", ";\n  }\n\n  ", ";\n"], ["\n  margin: ", " 0;\n  height: ", ";\n  position: relative;\n  text-align: center;\n  background-color: ", ";\n  display: flex;\n  justify-content: center;\n  align-items: center;\n\n  &:before {\n    position: absolute;\n    content: \"\";\n    left: 0;\n    right: 0;\n    top: calc(50% - 1px);\n    height: 1px;\n    background-color: ", ";\n  }\n\n  ",
    ";\n"])), zord(4), ifProp('text', '16px', 0), color('surfaceBackgroundSecondary'), color('surfaceBorder'), ifProp('text', css(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n            &:after {\n              position: absolute;\n              height: 16px;\n              padding: 0 2px;\n              font-weight: 500;\n              font-size: 13px;\n              content: \"", "\";\n              display: block;\n              justify-content: center;\n              background-color: ", ";\n              color: ", ";\n            }\n          "], ["\n            &:after {\n              position: absolute;\n              height: 16px;\n              padding: 0 2px;\n              font-weight: 500;\n              font-size: 13px;\n              content: \"", "\";\n              display: block;\n              justify-content: center;\n              background-color: ", ";\n              color: ", ";\n            }\n          "])), prop('text'), color('surfaceBackgroundSecondary'), function (_a) {
    var theme = _a.theme;
    return theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surfaceIcon;
})));
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13;
